import { useMediaQuery } from "@mui/material";
import { ReactNode, useEffect, useRef, useState } from 'react';

import BTDrawer from '@common-components/bt-drawer/bt-drawer';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { theme } from '@common/theme';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import classes from "./tasks-panel.module.scss";

interface ResizableDrawerProps {
    children: ReactNode;
    isOpen: boolean;
    onClose?: () => void;
    drawerWidthProp?: number;
    stopResize?: (drawerWidth: number) => void;
    showToggle?: boolean;
    onToggle?: () => void;
    toggleIcon?: ReactNode;
    contentOverflow?: "auto" | "hidden";
}

const ResizableDrawer = ({
    children,
    isOpen,
    onClose,
    drawerWidthProp,
    stopResize,
    showToggle,
    onToggle,
    toggleIcon,
    contentOverflow = "auto"
}: ResizableDrawerProps) => {

    const isDesktop = useMediaQuery(theme.breakpoints.up(786))
    const direction = useAppSelector((state) => state.UserReducer.direction);

    const [drawerWidth, setDrawerWidth] = useState<number>();
    const drawerWidthRef = useRef<number>();
    const startResizing = (e: React.MouseEvent) => {
        e.preventDefault();
        document.addEventListener('mousemove', resizeDrawer);
        document.addEventListener('mouseup', stopResizing);
    };

    const resizeDrawer = (e: MouseEvent) => {
        const windowWidth = window.innerWidth;
        const currentMax = isOpen ? 310 : 90
        const maxWidth = windowWidth - currentMax
        
        // Calculate width based on direction
        let newWidth;
        if (direction === "rtl") {
            // For RTL, calculate from left edge
            newWidth = e.clientX;
        } else {
            // For LTR, calculate from right edge
            newWidth = windowWidth - e.clientX;
        }

        newWidth = Math.max(newWidth, 500);
        newWidth = Math.min(newWidth, maxWidth);

        setDrawerWidth(newWidth);
        drawerWidthRef.current = newWidth;
    };
    const stopResizing = () => {
        if (stopResize && drawerWidthRef.current !== undefined) {
            stopResize(drawerWidthRef.current);
        }

        document.removeEventListener('mousemove', resizeDrawer);
        document.removeEventListener('mouseup', stopResizing);
    };

    function handleEscapeKeyClose(e: KeyboardEvent) {
        if (e.key === "Escape" && onClose) {
            onClose();
        }
    }

    useEffect(() => {
        const initialWidth = drawerWidthProp ? drawerWidthProp : 500;
        setDrawerWidth(initialWidth);
        drawerWidthRef.current = initialWidth;
        document.addEventListener("keydown", handleEscapeKeyClose);
        return () => {
            document.removeEventListener("keydown", handleEscapeKeyClose);
        };
    }, [drawerWidthProp, onClose]);

    useEffect(() => {
        return () => {
            document.removeEventListener('mousemove', resizeDrawer);
            document.removeEventListener('mouseup', stopResizing);
        };
    }, []);

    return (
        <BTDrawer 
            variant={isDesktop ? "persistent" : 'temporary'} 
            anchor={direction === "ltr" ? 'right' : 'left'} 
            open={isOpen}
            showToggle={showToggle}
            onToggle={onToggle}
            toggleIcon={toggleIcon}
        >
            <div className={classes.extraDataPanel} style={{
                overflow: contentOverflow,
                height: "100%",
                width: isDesktop ? drawerWidth : '100vw',
                position: "relative"
            }}>
                <div className={`${classes.resizeHandle}`} onMouseDown={startResizing}>
                    <DragIndicatorIcon sx={{ fontSize: "16px", color: "var(--color-text)" }} />
                </div>
                {children}
            </div>
        </BTDrawer>
    );
};

export default ResizableDrawer;
